import { supabase } from './supabase'
import type { Tournament } from './tournaments'
import { SOCIAL_LABEL, SOCIAL_PLATFORMS, type SocialPlatform } from './social-platforms'

/**
 * Server-side data access for organizer profiles (/en/org/<slug>).
 *
 * Organizers imported from Battlefy and Challengermode live in their own
 * `organizations` table rather than as `users` rows — see the header of
 * 20260912000000_organizations.sql for why. Every column is either SYNCED
 * (the daily ingester owns it) or *_override (only a claimer writes it), and
 * the reads below resolve the pair so callers never see the split.
 */

/** Raw row shape as stored in Postgres. */
interface OrganizationRow {
  id: string
  slug: string
  source: string | null
  external_id: string | null
  name: string
  bio: string | null
  logo_url: string | null
  banner_url: string | null
  name_override: string | null
  bio_override: string | null
  logo_url_override: string | null
  banner_url_override: string | null
  socials: Record<string, string> | null
  socials_override: Record<string, string> | null
  platform_verified: boolean
  followers: number | null
  events_hosted: number | null
  upstream_created_at: string | null
  external_url: string | null
  claimed_by: string | null
  claimed_at: string | null
}

/** Socials we render, in display order. The upstream set is ragged. */
export const ORG_SOCIAL_PLATFORMS = SOCIAL_PLATFORMS
export const ORG_SOCIAL_LABEL = SOCIAL_LABEL
export type OrgSocialPlatform = SocialPlatform

export interface OrgSocial {
  platform: OrgSocialPlatform
  url: string
}

export interface Organization {
  id: string
  slug: string
  /** 'battlefy' | 'challengermode', or null for a GamerPlug-native organizer. */
  source: string | null
  name: string
  bio: string | null
  logo: string | null
  banner: string | null
  socials: OrgSocial[]
  /** The upstream platform's badge — never a GamerPlug review. */
  platformVerified: boolean
  followers: number | null
  eventsHosted: number | null
  /** ISO string; "Organizer since" on the profile. */
  upstreamCreatedAt: string | null
  externalUrl: string | null
  claimed: boolean
}

/** Human label for the source platform. */
export function sourceLabel(source: string | null): string | null {
  if (source === 'battlefy') return 'Battlefy'
  if (source === 'challengermode') return 'Challengermode'
  return null
}

/**
 * Resolve the owner override against the synced value. Mirrors the
 * organization_display_* SQL functions so a client-side read agrees with a
 * server-side one.
 */
function mapRow(row: OrganizationRow): Organization {
  const socialBag = row.socials_override ?? row.socials ?? {}
  const socials = ORG_SOCIAL_PLATFORMS.flatMap((platform) => {
    const url = socialBag[platform]
    // Only absolute URLs are rendered. The ingester normalizes handles to
    // URLs on write, but a backfilled row could still carry something odd.
    return typeof url === 'string' && /^https?:\/\//i.test(url)
      ? [{ platform, url }]
      : []
  })

  return {
    id: row.id,
    slug: row.slug,
    source: row.source,
    name: row.name_override ?? row.name,
    bio: row.bio_override ?? row.bio,
    logo: row.logo_url_override ?? row.logo_url,
    banner: row.banner_url_override ?? row.banner_url,
    socials,
    platformVerified: row.platform_verified,
    followers: row.followers,
    eventsHosted: row.events_hosted,
    upstreamCreatedAt: row.upstream_created_at,
    externalUrl: row.external_url,
    claimed: row.claimed_by !== null,
  }
}

const COLUMNS =
  'id, slug, source, external_id, name, bio, logo_url, banner_url, name_override, bio_override, logo_url_override, banner_url_override, socials, socials_override, platform_verified, followers, events_hosted, upstream_created_at, external_url, claimed_by, claimed_at'

/** One organizer by URL slug. Null when unknown, so the caller can 404. */
export async function getOrganizationBySlug(
  slug: string
): Promise<Organization | null> {
  const { data, error } = await supabase
    .from('organizations')
    .select(COLUMNS)
    .eq('slug', slug)
    .maybeSingle()

  if (error) {
    console.error('getOrganizationBySlug error:', error.message)
    return null
  }
  return data ? mapRow(data as unknown as OrganizationRow) : null
}

/** Every organizer slug — for generateStaticParams and the sitemap. */
export async function getAllOrganizationSlugs(): Promise<string[]> {
  const { data, error } = await supabase.from('organizations').select('slug')

  if (error) {
    console.error('getAllOrganizationSlugs error:', error.message)
    return []
  }
  return (data ?? []).map((r) => r.slug as string)
}

/** The event columns the organizer page's list needs. */
const EVENT_COLUMNS =
  'id, slug, title, game_slug, game_name, starts_at, timezone, region, platform, format, team_size, max_participants, registered, prize_pool_cents, currency, status:status_effective'

export interface OrgEvent {
  id: string
  slug: string
  title: string
  gameSlug: string
  game: string
  startsAt: string
  timezone: string
  region: string
  platform: string
  format: string
  teamSize: number
  maxParticipants: number
  registered: number
  prizePoolCents: number
  currency: string
  status: Tournament['status']
}

/**
 * An organizer's events, soonest first.
 *
 * Reads the table directly rather than `list_tournaments`: that RPC returns
 * every event on the platform, and filtering 141+ rows in JS to find one
 * organizer's is wasteful when Postgres can do it on an index. RLS hides
 * private rows from the anon key, which is the desired behaviour here — a
 * private event should not be discoverable by browsing its organizer.
 */
export async function getOrganizationEvents(
  organizationId: string
): Promise<OrgEvent[]> {
  const { data, error } = await supabase
    .from('tournaments')
    .select(EVENT_COLUMNS)
    .eq('organization_id', organizationId)
    .order('starts_at', { ascending: true })

  if (error) {
    console.error('getOrganizationEvents error:', error.message)
    return []
  }

  return (data ?? []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    slug: row.slug as string,
    title: row.title as string,
    gameSlug: row.game_slug as string,
    game: row.game_name as string,
    startsAt: row.starts_at as string,
    timezone: row.timezone as string,
    region: row.region as string,
    platform: row.platform as string,
    format: row.format as string,
    teamSize: row.team_size as number,
    maxParticipants: row.max_participants as number,
    registered: row.registered as number,
    prizePoolCents: row.prize_pool_cents as number,
    currency: row.currency as string,
    status: row.status as Tournament['status'],
  }))
}

/**
 * Split an organizer's events into upcoming and past.
 *
 * Matches the browse list's rule (tournaments-client.tsx): an event that
 * already started today still counts as upcoming, except a completed one,
 * which always belongs to past. Upcoming sorts soonest first, past most
 * recent first.
 */
export function splitEvents(events: OrgEvent[]): {
  upcoming: OrgEvent[]
  past: OrgEvent[]
} {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const boundary = startOfToday.getTime()

  const isUpcoming = (e: OrgEvent) =>
    e.status !== 'completed' && new Date(e.startsAt).getTime() >= boundary

  const upcoming = events.filter(isUpcoming)
  const past = events
    .filter((e) => !isUpcoming(e))
    .sort(
      (a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()
    )

  return { upcoming, past }
}

/** Distinct games an organizer runs, most events first. */
export function organizationGames(
  events: OrgEvent[]
): { slug: string; name: string; count: number }[] {
  const byGame = new Map<string, { slug: string; name: string; count: number }>()
  for (const e of events) {
    const existing = byGame.get(e.gameSlug)
    if (existing) existing.count += 1
    else byGame.set(e.gameSlug, { slug: e.gameSlug, name: e.game, count: 1 })
  }
  return [...byGame.values()].sort((a, b) => b.count - a.count)
}
